/* eslint-disable @typescript-eslint/no-explicit-any */
'use client'

import { useState } from 'react'
import ShellButton from '@/components/ShellButton'

// Define the CustomSpecification interface
interface CustomSpecification {
  name: string;
  value: string;
}

interface TabData {
  [key: string]: {
    title: string;
    content?: CustomSpecification[] | Record<string, string | number | boolean | null> | string | null;
    articleSlug?: string;
    readMore?: string;
    labels?: {
      batchNumber: string;
      testDate: string;
      expirationDate: string;
      downloadCoa: string;
      na: string;
    };
  };
}

interface TabsClientProps {
  tabData: TabData;
  locale: string;
  formatSpecKey?: (key: string) => string;
}

// Helper function to format spec keys on the client side
function formatSpecKeyClient(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

export default function TabsClient({ 
  tabData, 
  locale,
  // We'll use the passed function if it's a simple string formatter,
  // otherwise we'll use our client-side implementation
  formatSpecKey = formatSpecKeyClient
}: TabsClientProps) {
  const [activeTab, setActiveTab] = useState(Object.keys(tabData)[0]);
  
  return (
    <>
      {/* Tabs Navigation */}
      <div className="mb-8 border-b border-border">
        <div className="flex flex-wrap -mb-px">
          {Object.entries(tabData).map(([key, data]) => (
            <button
              key={key}
              className={`mr-8 py-4 text-lg font-medium border-b-2 transition-colors ${
                activeTab === key
                  ? 'text-primary border-primary'
                  : 'text-text-main border-transparent hover:text-text-main hover:border-border'
              }`}
              onClick={() => setActiveTab(key)}
            >
              {data.title}
            </button>
          ))}
        </div>
      </div>
      
      {/* Tab Content */}
      <div className="py-4">
        {/* Description Tab */}
        {activeTab === 'description' && tabData.description && (
          <div className="prose prose-invert max-w-none">
            <p className="text-text-main leading-relaxed">
              {typeof tabData.description.content === 'string' 
                ? tabData.description.content 
                : JSON.stringify(tabData.description.content)}
            </p>
          </div>
        )}
        
        {/* Specifications Tab */}
        {activeTab === 'specifications' && tabData.specifications && (
          <div className="bg-surface/30 backdrop-blur-sm rounded-xl border border-border overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-6">
              {tabData.specifications.content && Array.isArray(tabData.specifications.content) ? (
                // Handle new CustomSpecification[] format
                tabData.specifications.content.map((spec: CustomSpecification, index) => (
                  <div key={index} className="flex justify-between py-2 border-b border-border">
                    <span className="text-text-secondary font-medium">{spec.name}</span>
                    <span className="text-text-main">{spec.value}</span>
                  </div>
                ))
              ) : tabData.specifications.content && typeof tabData.specifications.content === 'object' ? (
                // Handle legacy format (Record<string, string>)
                Object.entries(tabData.specifications.content).map(([key, value]) => (
                  <div key={key} className="flex justify-between py-2 border-b border-border">
                    <span className="text-text-secondary font-medium">{formatSpecKey(key)}</span>
                    <span className="text-text-main">{value as React.ReactNode}</span>
                  </div>
                ))
              ) : (
                <div className="py-2 text-text-secondary">No specifications available</div>
              )}
            </div>
          </div>
        )}
        
        {/* Test Results Tab */}
        {activeTab === 'testResults' && tabData.testResults && (
          <div className="bg-surface/30 backdrop-blur-sm rounded-xl border border-border overflow-hidden p-6">
            {tabData.testResults.content ? (
              <div className="flex flex-row justify-between items-stretch">
                {typeof tabData.testResults.content === 'object' && tabData.testResults.content !== null && 'batchNumber' in tabData.testResults.content && (
                  <div className="flex flex-col justify-center">
                    <span className="text-text-secondary font-medium md:w-1/4">{tabData.testResults.labels?.batchNumber}:</span>
                    <span className="text-text-main">{(tabData.testResults.content as Record<string, any>).batchNumber}</span>
                  </div>
                )}
                
                {typeof tabData.testResults.content === 'object' && tabData.testResults.content !== null && 'testDate' in tabData.testResults.content && (
                  <div className="flex flex-col justify-center">
                    <span className="text-text-secondary font-medium md:w-1/4">{tabData.testResults.labels?.testDate}:</span>
                    <span className="text-text-main">{(tabData.testResults.content as Record<string, any>).testDate}</span>
                  </div>
                )}
                
                {typeof tabData.testResults.content === 'object' && tabData.testResults.content !== null && 'expirationDate' in tabData.testResults.content && (
                  <div className="flex flex-col justify-center">
                    <span className="text-text-secondary font-medium md:w-1/4">{tabData.testResults.labels?.expirationDate}:</span>
                    <span className="text-text-main">{(tabData.testResults.content as Record<string, any>).expirationDate}</span>
                  </div>
                )}
                
                {typeof tabData.testResults.content === 'object' && tabData.testResults.content !== null && 'coa' in tabData.testResults.content && (
                  <div className="flex items-center">
                    <a 
                      href={(tabData.testResults.content as Record<string, any>).coa}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-4 py-2 bg-primary-light text-primary-dark rounded-lg border border-primary/30 hover:bg-primary-light/90 transition"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      {tabData.testResults.labels?.downloadCoa}
                    </a>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-text-secondary">{tabData.testResults.labels?.na}</p>
            )}
          </div>
        )}
        
        {/* Related Article Tab */}
        {activeTab === 'relatedArticle' && tabData.relatedArticle && (
          <div className="text-center">
            <p className="text-text-secondary mb-6">
              {tabData.relatedArticle.readMore}
            </p>
            <ShellButton
              onClick={() => window.location.href = `/${locale}/articles/${tabData.relatedArticle.articleSlug}`}
              variant="gradient"
            >
              Read Article
            </ShellButton>
          </div>
        )}
      </div>
    </>
  );
} 